import { ref } from 'vue'
import { httpService } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { SubscriptionTerms_TermRow } from '../contracts/SubscriptionTerms_TermRow'
import type { SubscriptionTerms_ListResponseDados } from '../contracts/SubscriptionTerms_ListResponse'
import type { SubscriptionTerms_SaveInput } from '../contracts/SubscriptionTerms_SaveInput'
import type { SubscriptionTerms_TermIdInput } from '../contracts/SubscriptionTerms_TermIdInput'
import type { SubscriptionTerms_VariantSaveInput } from '../contracts/SubscriptionTerms_VariantSaveInput'
import type { SubscriptionTerms_AcceptanceRow } from '../contracts/SubscriptionTerms_AcceptanceRow'
import type { SubscriptionTerms_AcceptancesQueryInput } from '../contracts/SubscriptionTerms_AcceptancesQueryInput'
import type { SubscriptionTerms_AcceptancesResponseDados } from '../contracts/SubscriptionTerms_AcceptancesResponse'
import type { SubscriptionTerms_AcceptanceDetailResponseDados } from '../contracts/SubscriptionTerms_AcceptanceDetailResponse'
import type { SubscriptionTerms_CurrentInput } from '../contracts/SubscriptionTerms_CurrentInput'
import type { SubscriptionTerms_CurrentResponseDados } from '../contracts/SubscriptionTerms_CurrentResponse'
import type { SubscriptionTerms_AcceptInput } from '../contracts/SubscriptionTerms_AcceptInput'
import type { SubscriptionTerms_AcceptResponseDados } from '../contracts/SubscriptionTerms_AcceptResponse'
// Import side-effects: registers the contracts with the responseContract registry.
import '../contracts/SubscriptionTerms_TermRow'
import '../contracts/SubscriptionTerms_ListResponse'
import '../contracts/SubscriptionTerms_SaveInput'
import '../contracts/SubscriptionTerms_TermIdInput'
import '../contracts/SubscriptionTerms_VariantSaveInput'
import '../contracts/SubscriptionTerms_AcceptanceRow'
import '../contracts/SubscriptionTerms_AcceptancesQueryInput'
import '../contracts/SubscriptionTerms_AcceptancesResponse'
import '../contracts/SubscriptionTerms_AcceptanceDetailResponse'
import '../contracts/SubscriptionTerms_CurrentInput'
import '../contracts/SubscriptionTerms_CurrentResponse'
import '../contracts/SubscriptionTerms_AcceptInput'
import '../contracts/SubscriptionTerms_AcceptResponse'

/**
 * useSubscriptionTerms — SOLE network owner for `subscriptions/admin-terms/*`
 * (staff) and `subscriptions/terms/*` (franchisee).
 *
 * REFUSALS ARRIVE AS HTTP 200. `sendError()` never touches the HTTP status, so
 * `httpService` does not throw and `validateResponse()` does not even run — the
 * ENVELOPE's `success:false` is the only refusal signal. Every mutation reads
 * `success` from INSIDE the body before trusting it.
 *
 * The refusal CODE is rethrown verbatim, never replaced with a friendly
 * sentence. Callers map it through their own humanize* function so the screen
 * can say which rule fired: 'termo_possui_aceites' and
 * 'assinatura_formato_invalido' need different answers from the person reading
 * them, and a generic "erro ao salvar" hides both.
 *
 * `languages` comes down WITH the rows, from the translations-service. The
 * console must never build that list locally — a second, smaller list is what
 * once let an operator mark a user as pt-PT and then find no template could be
 * written for them.
 *
 * PLANS ARE NOT OWNED HERE. They belong to useSubscriptionAdmin, which reads
 * GET /subscriptions/plans. A second copy of the catalogue is how a selector
 * drifts from the thing it names; the term row carries the plan NAME only so
 * the table can label a row without a second round trip.
 *
 * A TERM ROW IS ONE VERSION OF ONE PLAN, carrying its language variants. There
 * is at most one live version per plan, whatever languages it holds: the
 * variants are translations of the same agreement, not agreements of their own.
 *
 * Module-scope singleton, like every other composable in this domain.
 * Call `_reset()` in afterEach for test isolation.
 */

// ── Module-scope singleton state ────────────────────────────────────────────

const terms = ref<SubscriptionTerms_TermRow[]>([])
const acceptances = ref<SubscriptionTerms_AcceptanceRow[]>([])
/** Idiomas em que um termo pode ser escrito — vem junto com as linhas. */
const languages = ref<string[]>([])
// ── Internal helpers ─────────────────────────────────────────────────────────

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

/**
 * Splice one version into the list.
 *
 * Publishing retires the predecessor OF THE SAME PLAN, and the server echoes
 * only the row that was activated — so the stale sibling is cleared here
 * rather than left showing two live agreements for one plan until the next
 * full load. Language plays no part: a plan has one agreement in force,
 * whatever translations it carries.
 */
function upsertTerm(row: SubscriptionTerms_TermRow): void {
  const next = terms.value.map((r) => {
    if (r.id === row.id) return row
    if (row.ativo && r.ativo && r.plan_id === row.plan_id) {
      return { ...r, ativo: false }
    }
    return r
  })

  terms.value = next.some((r) => r.id === row.id) ? next : [...next, row]
}

// ── Public composable ────────────────────────────────────────────────────────

export function useSubscriptionTerms() {
  // ── Staff console ─────────────────────────────────────────────────────────

  /** listTerms — GET subscriptions/admin-terms/list. Full-only. */
  async function listTerms(): Promise<void> {
    const raw = await httpService.get(
      sistemaUrl('subscriptions/admin-terms/list'),
      { responseContract: 'SubscriptionTerms_ListResponse', skipErrorDisplay: false },
    )
    const resp = raw as { dados: SubscriptionTerms_ListResponseDados }
    terms.value = resp.dados.rows
    languages.value = resp.dados.languages
    log('useSubscriptionTerms', 'listTerms', 'Termos carregados', { count: terms.value.length })
  }

  /**
   * saveTerm — POST subscriptions/admin-terms/save. `input.id` null = create.
   *
   * Creates or renames a VERSION and carries no text: the wording belongs to a
   * language variant, written through saveVariant(). A created version is
   * always a draft; publishing is activateTerm().
   *
   * Refusals: 'plano_nao_encontrado', 'termo_nao_encontrado'.
   */
  async function saveTerm(input: SubscriptionTerms_SaveInput): Promise<SubscriptionTerms_TermRow> {
    const body: SubscriptionTerms_SaveInput = {
      plan_id: input.plan_id,
      titulo: input.titulo,
      versao: input.versao,
      ...(input.id != null ? { id: input.id } : {}),
    }
    const raw = await httpService.post(
      sistemaUrl('subscriptions/admin-terms/save'),
      body,
      { inputContract: 'SubscriptionTerms_SaveInput', responseContract: 'SubscriptionTerms_ListResponse', skipErrorDisplay: false },
    )
    const resp = raw as { success?: boolean; dados: SubscriptionTerms_ListResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'subscription_term_save_failed')
    }
    const row = resp.dados.rows[0]
    upsertTerm(row)
    log('useSubscriptionTerms', 'saveTerm', 'Termo salvo', { id: row.id, ativo: row.ativo })
    return row
  }

  /**
   * saveVariant — POST subscriptions/admin-terms/variant-save.
   *
   * Writes one LANGUAGE of one version. Creating a translation and editing it
   * are the same call: (version, language) is unique.
   *
   * Refusals: 'termo_nao_encontrado', 'language_unsupported'.
   */
  async function saveVariant(input: SubscriptionTerms_VariantSaveInput): Promise<SubscriptionTerms_TermRow> {
    const raw = await httpService.post(
      sistemaUrl('subscriptions/admin-terms/variant-save'),
      input,
      { inputContract: 'SubscriptionTerms_VariantSaveInput', responseContract: 'SubscriptionTerms_ListResponse', skipErrorDisplay: false },
    )
    const resp = raw as { success?: boolean; dados: SubscriptionTerms_ListResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'subscription_term_variant_save_failed')
    }
    const row = resp.dados.rows[0]
    upsertTerm(row)
    log('useSubscriptionTerms', 'saveVariant', 'Variante salva', { term_id: row.id, language: input.language })
    return row
  }

  /**
   * activateTerm — POST subscriptions/admin-terms/activate.
   * Refusals: 'termo_nao_encontrado', 'falha_ao_ativar_termo'.
   */
  async function activateTerm(id: number): Promise<SubscriptionTerms_TermRow> {
    const body: SubscriptionTerms_TermIdInput = { id }
    const raw = await httpService.post(
      sistemaUrl('subscriptions/admin-terms/activate'),
      body,
      { inputContract: 'SubscriptionTerms_TermIdInput', responseContract: 'SubscriptionTerms_ListResponse', skipErrorDisplay: false },
    )
    const resp = raw as { success?: boolean; dados: SubscriptionTerms_ListResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'subscription_term_activate_failed')
    }
    const row = resp.dados.rows[0]
    upsertTerm(row)
    log('useSubscriptionTerms', 'activateTerm', 'Termo publicado', { id: row.id })
    return row
  }

  /**
   * deleteTerm — POST subscriptions/admin-terms/delete.
   * Refusals: 'termo_nao_encontrado', 'termo_possui_aceites' (evidence points
   * at it — surface the code, don't retry).
   */
  async function deleteTerm(id: number): Promise<void> {
    const body: SubscriptionTerms_TermIdInput = { id }
    const raw = await httpService.post(
      sistemaUrl('subscriptions/admin-terms/delete'),
      body,
      { inputContract: 'SubscriptionTerms_TermIdInput', responseContract: 'SubscriptionTerms_ListResponse', skipErrorDisplay: false },
    )
    const resp = raw as { success?: boolean; dados: SubscriptionTerms_ListResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'subscription_term_delete_failed')
    }
    terms.value = resp.dados.rows
    log('useSubscriptionTerms', 'deleteTerm', 'Termo excluído', { id })
  }

  /** listAcceptances — POST subscriptions/admin-terms/acceptances. */
  async function listAcceptances(
    query: SubscriptionTerms_AcceptancesQueryInput = { limit: 100, offset: 0 },
  ): Promise<void> {
    const raw = await httpService.post(
      sistemaUrl('subscriptions/admin-terms/acceptances'),
      query,
      { inputContract: 'SubscriptionTerms_AcceptancesQueryInput', responseContract: 'SubscriptionTerms_AcceptancesResponse', skipErrorDisplay: false },
    )
    const resp = raw as { success?: boolean; dados: SubscriptionTerms_AcceptancesResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'subscription_term_acceptances_failed')
    }
    acceptances.value = resp.dados.rows
    log('useSubscriptionTerms', 'listAcceptances', 'Aceites carregados', { count: acceptances.value.length })
  }

  /**
   * getAcceptance — GET subscriptions/admin-terms/acceptance/{id}.
   *
   * The heavy read: the agreement text as signed plus the signature image.
   * NOT stored in module state — a base64 image kept in a singleton would be
   * carried by every later render of the listing for no reason.
   */
  async function getAcceptance(id: number): Promise<SubscriptionTerms_AcceptanceDetailResponseDados> {
    const raw = await httpService.get(
      sistemaUrl(`subscriptions/admin-terms/acceptance/${id}`),
      { responseContract: 'SubscriptionTerms_AcceptanceDetailResponse', skipErrorDisplay: false },
    )
    const resp = raw as { success?: boolean; dados: SubscriptionTerms_AcceptanceDetailResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'aceite_nao_encontrado')
    }
    return resp.dados
  }

  // ── Franchisee ────────────────────────────────────────────────────────────

  /**
   * currentTerm — POST subscriptions/terms/current.
   *
   * The agreement hangs off the PLAN, so the caller names both the franchise
   * it acts for and the plan it is about to subscribe.
   *
   * Refusals: 'franquia_id fora do escopo permitido',
   * 'termos_indisponiveis_para_o_plano'.
   */
  async function currentTerm(
    franquiaId: number,
    planId: number,
  ): Promise<SubscriptionTerms_CurrentResponseDados> {
    const body: SubscriptionTerms_CurrentInput = { franquia_id: franquiaId, plan_id: planId }
    const raw = await httpService.post(
      sistemaUrl('subscriptions/terms/current'),
      body,
      { inputContract: 'SubscriptionTerms_CurrentInput', responseContract: 'SubscriptionTerms_CurrentResponse', skipErrorDisplay: false },
    )
    const resp = raw as { success?: boolean; dados: SubscriptionTerms_CurrentResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'termos_indisponiveis_para_o_plano')
    }
    return resp.dados
  }

  /**
   * acceptTerm — POST subscriptions/terms/accept.
   *
   * Refusals: 'termo_nao_encontrado', 'termo_nao_esta_ativo',
   * 'assinatura_ausente', 'assinatura_formato_invalido',
   * 'assinatura_muito_grande', 'assinatura_insuficiente'.
   *
   * Idempotent server-side per (franchise, document): a second click on a slow
   * connection returns the first acceptance instead of a second agreement.
   */
  async function acceptTerm(input: SubscriptionTerms_AcceptInput): Promise<SubscriptionTerms_AcceptResponseDados> {
    const raw = await httpService.post(
      sistemaUrl('subscriptions/terms/accept'),
      input,
      { inputContract: 'SubscriptionTerms_AcceptInput', responseContract: 'SubscriptionTerms_AcceptResponse', skipErrorDisplay: false },
    )
    const resp = raw as { success?: boolean; dados: SubscriptionTerms_AcceptResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'subscription_term_accept_failed')
    }
    log('useSubscriptionTerms', 'acceptTerm', 'Termo aceito', { term_id: resp.dados.term_id })
    return resp.dados
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    terms.value = []
    acceptances.value = []
    languages.value = []
  }

  return {
    terms,
    acceptances,
    languages,
    listTerms,
    saveTerm,
    saveVariant,
    activateTerm,
    deleteTerm,
    listAcceptances,
    getAcceptance,
    currentTerm,
    acceptTerm,
    _reset,
  }
}
